import { useCallback, useState } from "react";
import { z } from "zod";

import {
  API_CREATE_DESIGNATION,
  API_UPDATE_DESIGNATION,
  GET_DESIGNATION_LIST,
} from "../../../../../config/Endpoints.js";
import {
  ApiError,
  GENERIC_ERROR,
  getApi,
  postApi,
  putApi,
} from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import type { DesignationRow, MutationResult } from "../types.ts";
import { extractApiMessage } from "../utils/extract-api-message.ts";
import { unwrapApiDataArray } from "../utils/unwrap-api-data.ts";

const designationRowSchema = z.object({
  id: z.union([z.number(), z.string()]).transform((v) => Number(v)),
  name: z.string(),
  creationDate: z.string().nullish(),
  status: z.string().nullish(),
  departmentId: z.union([z.number(), z.string()]).nullish(),
});

function parseDesignations(raw: unknown): DesignationRow[] {
  const items = unwrapApiDataArray<unknown>(raw);
  const parsed = z.array(designationRowSchema).safeParse(items);
  if (!parsed.success) return [];
  return parsed.data.map((row) => ({
    ...row,
    departmentId:
      row.departmentId != null ? Number(row.departmentId) : undefined,
  }));
}

export function useDesignations() {
  const [designations, setDesignations] = useState<DesignationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [departmentId, setDepartmentId] = useState<string | null>(null);

  const fetchDesignations = useCallback(
    async (deptId: string, signal?: AbortSignal) => {
      if (!deptId) {
        setDesignations([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const url = `${GET_DESIGNATION_LIST}?departmentId=${encodeURIComponent(deptId)}`;
        const { data } = await getApi<unknown>(
          url,
          accessToken(),
          "json",
          signal,
        );
        setDesignations(parseDesignations(data));
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : GENERIC_ERROR;
        setError(msg);
        setDesignations([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const selectDepartment = useCallback(
    (deptId: string | null) => {
      setDepartmentId(deptId);
      if (!deptId) {
        setDesignations([]);
        return;
      }
      void fetchDesignations(deptId);
    },
    [fetchDesignations],
  );

  const createDesignation = useCallback(
    async (name: string, deptId: string): Promise<MutationResult> => {
      setSubmitting(true);
      try {
        const { data } = await putApi(API_CREATE_DESIGNATION, accessToken(), {
          name: name.trim(),
          departmentId: Number(deptId),
        });
        await fetchDesignations(deptId);
        return { ok: true, message: extractApiMessage(data) };
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : GENERIC_ERROR;
        return { ok: false, message: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [fetchDesignations],
  );

  const updateDesignation = useCallback(
    async (id: number, name: string, deptId: string): Promise<MutationResult> => {
      setSubmitting(true);
      try {
        const { data } = await postApi(API_UPDATE_DESIGNATION, accessToken(), {
          id,
          name: name.trim(),
        });
        await fetchDesignations(deptId);
        return { ok: true, message: extractApiMessage(data) };
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : GENERIC_ERROR;
        return { ok: false, message: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [fetchDesignations],
  );

  return {
    designations,
    departmentId,
    loading,
    submitting,
    error,
    selectDepartment,
    refetch: () =>
      departmentId ? fetchDesignations(departmentId) : Promise.resolve(),
    createDesignation,
    updateDesignation,
  };
}
